/**
 * Forms module permission codes and role-based permission mapping.
 */

export const FORM_PERMISSION_CODES = {
  MANAGE: "forms.manage",
  VIEW_ALL: "forms.view_all",
  VIEW_ASSIGNED: "forms.view_assigned",
  PASS: "forms.pass",
  // The one real backend code for viewing stats/results (apps/forms/views/forms.py
  // action_perms and method_perms). RESULTS_VIEW_GROUPS/RESULTS_VIEW_ALL below do
  // not exist on the backend at all - see the ROLE_PERMISSIONS comment.
  VIEW_RESULTS: "forms.view_results",
  // Opening/closing survey windows for your own school (accounts migration
  // 0033, bound to the PSYCHOLOGIST position); apps/forms/views/windows.py.
  WINDOW_MANAGE: "forms.window.manage",
  RESULTS_VIEW_GROUPS: "forms.results.view_groups",
  RESULTS_VIEW_ALL: "forms.results.view_all",
};

export const TEST_RESULT_PERMISSION_CODES = {
  VIEW_SELF: "tests.results.view_self",
  VIEW_STAFF: "tests.results.view_staff",
  VIEW_STUDENTS: "tests.results.view_students",
  VIEW_GROUPS: "tests.results.view_groups",
  // Recording a whole class from a spreadsheet, for methods administered on
  // paper. Its own right, not part of the view set: seeded to PSYCHOLOGIST
  // alone (accounts migration 0029), and both upload endpoints gate on it.
  MANUAL_UPLOAD: "tests.results.manual_upload",
};

export const PROFILE_PERMISSION_CODES = {
  VIEW_ALL: "accounts.student_profile.view_all",
  VIEW_OWN: "accounts.student_profile.view_own",
  EDIT_ALL: "accounts.student_profile.edit_all",
  EDIT_OWN: "accounts.student_profile.edit_own",
  // The .xlsx bulk import, and ONLY that — a separate right rather than a
  // shorthand for the edit pair, because the template carries student names
  // and only PSYCHOLOGIST holds student_pii.view_own (accounts migration
  // 0026 grants it to that position alone). A TEACHER or METHODIST would
  // download a template of masked names, so the feature is theirs to see at
  // all. edit_own/edit_all still decide WHICH students a holder may write.
  BULK_IMPORT: "accounts.student_profile.bulk_import",
};

// Finding a student by their ID (/auth/students/lookup/). Its own right,
// seeded to the METHODIST position alone (accounts migration 0036). It finds a
// student and nothing more: the profile and correction-sheet links it offers
// keep their own gates (PROFILE_PERMISSION_CODES and
// MODULE_PERMISSION_CODES.RISK_GROUP), checked where those links render.
export const STUDENT_LOOKUP_PERMISSION_CODE = "accounts.students.lookup";

// Account management module. manage_all = platform admin (any school,
// may create managers); manage_school = school manager (own school only);
// org.manage_schools = may create/edit schools.
export const MANAGEMENT_PERMISSION_CODES = {
  MANAGE_ALL: "accounts.manage_all",
  MANAGE_SCHOOL: "accounts.manage_school",
  MANAGE_SCHOOLS: "org.manage_schools",
  // Editing your OWN staff positions. Deliberately not implied by MANAGE_SCHOOL
  // (accounts migration 0025 binds it to its own role, carried by the MANAGER
  // position) — the right to arrange your own posts is revocable without
  // touching the ability to manage a school, so it is checked on its own.
  OWN_POSITIONS: "accounts.own_positions.manage",
  // Assigning psychologists to a class. Its own role (accounts migration 0028,
  // carried by the MANAGER position, plus the platform_admin role), so it is
  // checked on its own and deliberately NOT folded into MANAGEMENT_USERS_PERMS
  // — same shape as OWN_POSITIONS above.
  //
  // Necessary but not sufficient: the endpoint's queryset is scoped to schools
  // the caller may manage, so a holder without manage_school/manage_all gets
  // 404 on every class rather than 403. MANAGER holds both.
  ASSIGN_CLASS_PSYCHOLOGISTS: "accounts.class_psychologists.assign",
};

/**
 * What each navigable module needs, taken from the permissions its own
 * endpoints check (`method_perms` in the matching backend app).
 *
 * OR semantics throughout, because that is what the backend does:
 * `method_perms` is `any(user.has_perm(code))`. Own-vs-all is deliberately NOT
 * distinguished — `Account.has_perm` ignores the object, so which rows a
 * holder actually sees is decided by the queryset filter, never by the client.
 * Gate on (X_own OR X_all) and let the server draw the line.
 *
 * The point of listing them here rather than inside the sidebar is that the
 * route guards need the same answer: a menu entry that leads to a guard the
 * user cannot pass is worse than no entry at all.
 */
export const MODULE_PERMISSION_CODES = {
  // /test — TestModelViewSet.method_perms["GET"] in apps/tests/views/test.py
  TEST: ["tests.view_all", "tests.view_open", "tests.view_assigned"],
  // /schools — OrganizationsStatsListAPIView.method_perms in
  // apps/tests/views/test.py; tests.stats.view_all is the regional coordinator's.
  SCHOOLS: ["tests.view_all", "tests.stats.view_all"],
  // /test-coverage — apps/tests/views/coverage.py
  TEST_COVERAGE: ["tests.view_all", "tests.view_open"],
  // Kept identical to the /forms RouteGuard in App.js so the menu and the
  // guard can never disagree. Matches FormModelViewSet.action_perms["list"]
  // in apps/forms/views/forms.py exactly - VIEW_ALL/VIEW_ASSIGNED used to sit
  // here too, but those codes don't exist on the backend (see ROLE_PERMISSIONS
  // below) and VIEW_RESULTS was missing, so a role holding only
  // forms.view_results (no pass/manage) would have been denied the menu entry.
  FORMS: [
    FORM_PERMISSION_CODES.MANAGE,
    FORM_PERMISSION_CODES.PASS,
    FORM_PERMISSION_CODES.VIEW_RESULTS,
  ],
  CONSULTATION: [
    "consultation.records.view_all",
    "consultation.records.view_own",
  ],
  OBSERVATION: ["observation.records.view_all", "observation.records.view_own"],
  TRAINING: ["training.records.view_all", "training.records.view_own"],
  MEETINGS: [
    "administration_meetings.records.view_all",
    "administration_meetings.records.view_own",
  ],
  RISK_GROUP: [
    "riskzone.support_group.view_all",
    "riskzone.support_group.view_own",
  ],
};

// The zones a reader is shown at all, one code per zone (backend
// riskzone.queries.zone_permission, accounts migration 0043): a regional
// coordinator sees the red cases only, a principal red and orange - in the
// support-group module and on Home's card, which the server narrows itself.
const SUPPORT_GROUP_ZONES = ["red_zone", "orange_zone", "yellow_zone"];
const LISTED_ZONES = [...SUPPORT_GROUP_ZONES, "green_zone"];

export const getListedZones = (user) => {
  const perms = getUserPermissions(user);
  return LISTED_ZONES.filter((code) =>
    perms.includes(`riskzone.support_group.${code}`),
  );
};

// Whether the module shows this reader only part of the support group.
export const isListNarrowed = (listedZones) =>
  SUPPORT_GROUP_ZONES.some((code) => !listedZones.includes(code));

export const MANAGEMENT_ANY = [
  MANAGEMENT_PERMISSION_CODES.MANAGE_ALL,
  MANAGEMENT_PERMISSION_CODES.MANAGE_SCHOOL,
  MANAGEMENT_PERMISSION_CODES.MANAGE_SCHOOLS,
];

export const MANAGEMENT_USERS_PERMS = [
  MANAGEMENT_PERMISSION_CODES.MANAGE_ALL,
  MANAGEMENT_PERMISSION_CODES.MANAGE_SCHOOL,
];

export const ROLE_PERMISSIONS = {
  test_coordinator: [
    FORM_PERMISSION_CODES.MANAGE,
    FORM_PERMISSION_CODES.VIEW_ALL,
    FORM_PERMISSION_CODES.RESULTS_VIEW_GROUPS,
    FORM_PERMISSION_CODES.RESULTS_VIEW_ALL,
  ],
  school_test_admin: [
    FORM_PERMISSION_CODES.VIEW_ASSIGNED,
    FORM_PERMISSION_CODES.PASS,
    FORM_PERMISSION_CODES.RESULTS_VIEW_GROUPS,
  ],
  school_test_viewer: [
    FORM_PERMISSION_CODES.RESULTS_VIEW_GROUPS,
  ],
  school_admin: [
    FORM_PERMISSION_CODES.VIEW_ASSIGNED,
    FORM_PERMISSION_CODES.PASS,
  ],
  student: [
    FORM_PERMISSION_CODES.VIEW_ASSIGNED,
    FORM_PERMISSION_CODES.PASS,
  ],
};

/**
 * Get the role codes from a user object.
 */
export const getUserRoleCodes = (user) => {
  return (user?.roles || [])
    .map((r) => String(r?.code || "").toLowerCase())
    .filter(Boolean);
};

/**
 * Get all permissions a user has, combining API-provided perms
 * with role-based permissions derived from ROLE_PERMISSIONS.
 */
export const getUserPermissions = (user) => {
  const apiPerms = Array.isArray(user?.perms) ? [...user.perms] : [];
  const roleCodes = getUserRoleCodes(user);
  const rolePerms = roleCodes.flatMap((code) => ROLE_PERMISSIONS[code] || []);
  return [...new Set([...apiPerms, ...rolePerms])];
};

export const hasPermission = (user, perm) => {
  if (!perm) return false;
  return getUserPermissions(user).includes(perm);
};

export const hasAnyPermission = (user, perms = []) => {
  if (!Array.isArray(perms) || perms.length === 0) return false;
  const all = getUserPermissions(user);
  return perms.some((p) => all.includes(p));
};
